import { useAppDispatch, useAppSelector } from 'appState/store'
import { ObGrid } from '@outbound-ai/ursa'
import { apiPaths, RelativeRootEnum } from 'configs/apiConfigs'
import { CSCreateQuery, CSRestQuery, Filter, Sort } from 'features/claims/claimTypes'
import { useEffect, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import { httpTenantsService } from 'utils/http'
import { updateCurrentUser } from '../userSlice'
import { USER_COLUMN_DEFS } from '../utils/UserConstants'
import { theme } from 'components/Theme'

export const UserList = () => {
  const [userList, setUserList] = useState<any[]>([])
  const { refresh, recentlyModifiedUserGuid, recentlyModifiedUserEmail } = useAppSelector((state) => state.user)
  const currentCustomer = useAppSelector((state) => state.auth.currentCustomer)
  const dispatch = useAppDispatch()
  const gridApiRef = useRef<any>(null)
  const [copiedCell, setCopiedCell] = useState<{ rowId: string; field: string } | null>(null)

  const getUserList = () => {
    if (!currentCustomer) {
      console.error('currentCustomer is undefined')
      return
    }

    const oaiCustomerQuery = new CSRestQuery(99998, 0)
    oaiCustomerQuery.addFilter(new Filter('customerId', '=', currentCustomer))
    oaiCustomerQuery.addSort(new Sort('created', true))
    const manageQuery = new CSCreateQuery(oaiCustomerQuery)
    const url = apiPaths.csSearch(RelativeRootEnum.cppuOaiUsers, manageQuery)

    httpTenantsService
      .get(url)
      .then((response) => {
        setUserList(response.data.data)
      })
      .catch((err) => {
        console.error('Error fetching user list:', err)
      })
  }

  // Clear copied cell highlight after 3 seconds
  useEffect(() => {
    if (copiedCell) {
      // Refresh cells immediately when copiedCell is set
      if (gridApiRef.current) {
        gridApiRef.current.refreshCells({ suppressFlash: true })
      }

      const timer = setTimeout(() => {
        setCopiedCell(null)
      }, 3000)
      return () => clearTimeout(timer)
    } else if (gridApiRef.current) {
      // Refresh cells when copiedCell is cleared
      gridApiRef.current.refreshCells({ suppressFlash: true })
    }
  }, [copiedCell])

  // Initial load when currentCustomer is available
  useEffect(() => {
    if (currentCustomer) {
      getUserList()
    }
  }, [currentCustomer])

  // Re-fetch when refresh is true
  useEffect(() => {
    if (refresh === true) {
      getUserList()
      dispatch(updateCurrentUser({ refresh: false }))
    }
  }, [refresh])

  // Clear highlight after 3 seconds
  useEffect(() => {
    if (recentlyModifiedUserGuid) {
      const timer = setTimeout(() => {
        dispatch(updateCurrentUser({ recentlyModifiedUserGuid: undefined, recentlyModifiedUserEmail: undefined }))
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [recentlyModifiedUserGuid, dispatch])

  // Navigate to page with highlighted user using email search
  useEffect(() => {
    if (recentlyModifiedUserEmail && gridApiRef.current && userList.length > 0) {
      const api = gridApiRef.current

      // Wait for grid to finish rendering
      setTimeout(() => {
        let foundRowIndex = -1

        // Find the row index by email in the displayed grid
        api.forEachNodeAfterFilterAndSort((node: any, index: number) => {
          if (node.data?.email === recentlyModifiedUserEmail) {
            foundRowIndex = index
          }
        })

        if (foundRowIndex !== -1) {
          const pageSize = api.paginationGetPageSize()
          const pageNumber = Math.floor(foundRowIndex / pageSize)
          api.paginationGoToPage(pageNumber)

          // Scroll to the row after page change
          setTimeout(() => {
            api.forEachNode((node: any) => {
              if (node.data?.email === recentlyModifiedUserEmail) {
                api.ensureIndexVisible(node.rowIndex, 'middle')
              }
            })
          }, 100)
        }
      }, 200)
    }
  }, [recentlyModifiedUserEmail, userList])

  function onModelUpdated(params: any) {
    const rowCount = params.api.getDisplayedRowCount()
    if (rowCount === 0) {
      params.api.showNoRowsOverlay()
    } else {
      params.api.hideOverlay()
    }
  }

  return (
    <GridWrapper>
      <ObGrid
        reactiveCustomComponents={true}
        columnDefs={USER_COLUMN_DEFS as any}
        rowData={userList}
        rowModelType="clientSide"
        paginationPageSize={10}
        paginationPageSizeSelector={[10, 20, 50, 100]}
        pagination={true}
        context={{ copiedCell, setCopiedCell, gridApiRef }}
        defaultColDef={{
          sortable: true,
          filter: true,
          resizable: true,
          floatingFilter: true,
          suppressMovable: true,
          suppressSizeToFit: true,
          menuTabs: ['filterMenuTab'],
        }}
        localeText={{ noRowsToShow: 'No users found' }}
        overlayNoRowsTemplate={'<span class="ag-overlay-no-rows-center">No users found</span>'}
        suppressNoRowsOverlay={false}
        suppressLoadingOverlay={false}
        onModelUpdated={onModelUpdated}
        onFilterChanged={onModelUpdated}
        getRowStyle={(params) => {
          if (params.data?.userGuid === recentlyModifiedUserGuid) {
            return {
              border: '1px solid #227bbb',
            }
          }
          return undefined
        }}
        onGridReady={async (params) => {
          gridApiRef.current = params.api
          const allColumnIds: string[] = []

          params.api.getColumns()?.forEach((column: any) => {
            if (column) {
              allColumnIds.push(column.getId())
            }
          })
          params.api.autoSizeColumns(allColumnIds, false)
        }}
        onRowClicked={(row) => {
          console.log('Row clicked:', row)
        }}
      />
    </GridWrapper>
  )
}

const GridWrapper = styled.div`
  ${({ theme }) => css`
    height: calc(
      100vh - ${theme.constants.navbarHeight} - ${theme.constants.pageTitleHeight} - ${theme.constants.tableHeadHeight} -
        120px
    );
  `}
  width: 100%;
  .ag-root-wrapper {
    height: 100% !important;
  }
  .ag-floating-filter-input {
    display: none;
  }
  .ag-row {
    overflow: visible !important;
  }
  .ag-cell {
    overflow: visible !important;
  }
  .ag-row-hover {
    z-index: 10 !important;
  }
  .highlighted-row {
    background-color: #fffacd !important;
    animation: fadeHighlight 3s ease-out;
  }
  .ag-center-cols-container .ag-row {
    border-right: none !important;
  }
  .ag-pinned-right-cols-container .ag-row {
    margin-top: 0 !important;
    border-left: none !important;
  }
  .ag-cell.cell-copied-highlight {
    background-color: ${theme.colors.primary.lightest} !important;
    border: 1px solid ${theme.colors.primary.neutral} !important;
  }
  @keyframes fadeHighlight {
    0% {
      background-color: #fffacd;
    }
    100% {
      background-color: transparent;
    }
  }
`
